/*=== CORE IMPORTS ===*/
import { html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { ViewBase } from '../view-base.js';
import { memberProfileViewStyles } from './member-profile-view-styles.js';

/*=== PACKAGE COMPONENTS ===*/
import '@shoelace-style/shoelace/dist/components/avatar/avatar.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/format-date/format-date.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';

/*=== Q1 COMPONENTS ===*/
import '../../components/q1-membership-chip/q1-membership-chip.js';
import '../../components/q1-spinner-panel/q1-spinner-panel.js';

/*=== LIBRARY MODULES ===*/
import orderBy from 'lodash/orderBy';
const _ = { orderBy };

/*=== Q1 MODULES ===*/
import { buildRefPathMap } from '../../modules/cache-helpers-client.js';
import { countLabel } from '../../modules/string-helpers-client.js';
import { enrichMember } from '../../modules/member-helpers-client.js';
import { enrichMembership } from '../../modules/membership-helpers-client.js';
import { q1MemberFunctions } from '../../modules/q1-core-functions.js';

/*=== CONSTANTS ===*/

const PLACE_TREE_CACHE = 'all-places-tree';

/*=== VIEW CLASS ===*/

export class MemberProfileView extends ViewBase {
  static styles = [
    ...super.styles,
    memberProfileViewStyles,
  ];

  static properties = {
    ...super.properties,

    memberNumber: { type: String },
    member: { type: Object },
    guildMembers: { type: Array },
    projectMembers: { type: Array },
    context: { type: Object },

    _refPathMap: { type: Object },
    
    _loading: { type: Boolean },
    _isError: { type: Boolean },
    _errorMessage: { type: String },
  };

  constructor() {
    super();
    this.memberNumber = null;
    this.member = {};
    
    this._loading = true;
    this._isError = false;
    this._errorMessage = null;
  }

  /*=== LIFECYCLE ===*/

  connectedCallback() {
    super.connectedCallback();
    
    this._initViewFromRouteParams();
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    if (changedProperties.has('routeParams')) {
      this._initViewFromRouteParams();
    }
  }

  _initViewFromRouteParams() {
    const newMemberNumber = this.routeParams?.key?.trim()?.toUpperCase();
    if (this.memberNumber === newMemberNumber) return;

    this._loading = true;
    this._isError = false;
    this._errorMessage = '';
    
    this.memberNumber = newMemberNumber;

    if (this.memberNumber?.length) {
      this.fetchMember();
    } else {
      this._errorMessage = 'Member number is missing from URL';
      this._isError = true;
      this._loading = false;
    }
  }

  setMemberStyles() {
    this.style.setProperty('--theme-color-primary', this.member.colorPrimary);
  }

  /*=== ACTIONS ===*/

  async fetchMember() {
    const { getMemberProfile } = q1MemberFunctions;

    this._loading = true;
    this._isError = false;
    this._errorMessage = null;

    try {
      const response = await getMemberProfile({ memberNumber: this.memberNumber })
      const responseData = response.data || {};

      const placeTreeCache = await this.appElement.cacheManager.getCache(PLACE_TREE_CACHE);

      // Build the refPathMap with places, guilds, and projects
      this._refPathMap = {
        ...buildRefPathMap(placeTreeCache.data, 'places', 'places'),
        ...Object.fromEntries(
          (responseData.guilds || []).map(guild => [`guilds/${guild.id}`, guild])
        ),
        ...Object.fromEntries(
          (responseData.projects || []).map(project => [`projects/${project.id}`, project])
        ),
      };
      
      this.member = enrichMember(responseData.member, this._refPathMap);
      this.setMemberStyles();
      this.setAfLink_db('members', this.member.id);
      this.context = responseData.context;

      this.guildMembers = this._sortMemberships(
        (responseData.guildMembers || []).map(gm => enrichMembership(gm, this._refPathMap))
      );
      this.projectMembers = this._sortMemberships(
        (responseData.projectMembers || []).map(pm => enrichMembership(pm, this._refPathMap))
      );

      this.setViewMeta();
    } catch (error) {
      console.error('Error getting member profile: ', error);

      switch (error.details?.resultCode) {
        case 'not-found':
          this.setViewMeta('Unavailable Member Profile  - q1 Network');
          this._errorMessage = 'Member profile not found or unavailable';
          break;

        default:
          this.setViewMeta('Member Profile Error - q1 Network');
          this._errorMessage = `Error getting member profile: ${error.message}`;
      }

      this._isError = true;
    } finally {
      this._loading = false;
    }
  }

  /*=== GETTERS ===*/

  get appHeaderVariant() {
    return 'white-text';
  }

  get hasBanner() {
    return !!this.member?.banner;
  }

  get hasLocations() {
    return this.member?.locationsPrimary_full?.length || this.member?.locationsOther_full?.length;
  }

  get isSelf() {
    return this.isSignedIn && this.currentMember?.id === this.member?.id; 
  }

  get isPrivateProfile() {
    return this.context?.viewPersona === 'public';
  }

  get guildCountLabel() {
    return countLabel(this.member?.guildCount || 0, 'guild');
  }

  get projectCountLabel() {
    return countLabel(this.member?.projectCount || 0, 'project');
  }

  get metaContext() {
    return { member: this.member };
  }

  /*=== TEMPLATES ===*/

  get contentTemplate() {
    if (this._loading) return html`
      <q1-spinner-panel label="Loading..." open></q1-spinner-panel>
    `;

    if (this._isError) return html`
      <h1>q1 Network Member Profile</h1>

      <p class="subtitle">
        ${this._errorMessage}
      </p>
    `;

    return html`
      ${this.profileHeaderTemplate}      

      <sl-divider class="full-width"></sl-divider>
      
      ${this.guildsTemplate}

      <sl-divider class="full-width"></sl-divider>
      
      ${this.projectsTemplate}
    `;
  }

  get profileHeaderTemplate() {
    return html`
      <profile-header>
        <profile-banner
          class=${classMap({ empty: !this.hasBanner })}
        >
          ${!this.hasBanner ? null : html`
            <img src=${this.member.banner?.standard?.url}/>
          `}  
        </profile-banner>

        <profile-bar class="sl-themed">
          <sl-avatar
            id="profileImage"
            label="${this.member.name}"
            image=${this.member.profile?.standard?.url || ''}
          ></sl-avatar>
          <button-bar>
            ${!this.member.linkedinProfileUrl?.length ? null : html`
              <sl-button
                href=${this.member.linkedinProfileUrl}
                target="_blank"
                variant="primary"
                size="large"
                pill
              >Linkedin Profile</sl-button>
            `}
          </button-bar>
        </profile-bar>
        
        ${this.memberCoreMetadataTemplate}

        ${this.memberStatsTemplate}

        ${this.locationListTemplate}
      </profile-header>
    `;
  }

  get memberCoreMetadataTemplate() {
    return html`
      <h1>${this.isPrivateProfile ? 'Private Profile' : this.member.name}</h1>
      
      <div id="memberNumber">
        @${this.member.number}
        <span>— ${this.member.level_label}</span>
      </div>
      
      ${this.isPrivateProfile ? null : html`
        <div id="subtitle">
          ${this.member.headline}
        </div>
      `}
    `;
  }

  get memberStatsTemplate() {
    if (this.isPrivateProfile) return null;

    return html`
      <ul id="member-stats">
        <li>
          <sl-icon library="material" name="calendar_month"></sl-icon>
          <span>Joined</span>
          <sl-format-date
            date=${this.member.createdAt}
            month="long"
            year="numeric"
          ></sl-format-date>
        </li>

        <li>
          <sl-icon library="material" name="hive"></sl-icon>
          <span>${this.guildCountLabel}</span>
        </li>

        <li>
          <sl-icon library="material" name="business_center"></sl-icon>
          <span>${this.projectCountLabel}</span>
        </li>
      </ul>
    `;
  }

  get locationListTemplate() {
    if (!this.hasLocations) return null;

    return html`
      <ul id="locations">
        ${this.member.locationsPrimary_full.map(location => html`
          <li class="primary">
            <sl-tooltip content="Primary Location">
              <a href="/members?locations=${location.id}">
                <sl-icon library="material" name="home_pin"></sl-icon>
                ${location.name}
              </a>
            </sl-tooltip>
          </li>
        `)}
        
        ${!this.member.locationsOther_full?.length ? null : html`
          <li class="other"> — </li>
        `}
        ${this.member.locationsOther_full.map(location => html`
          <li class="other">
            <sl-tooltip content="Other Location">
              <a href="/members?locations=${location.id}">
                <sl-icon library="material" name="location_on"></sl-icon>
                ${location.name}
              </a>
            </sl-tooltip>
          </li>
        `)}
      </ul>
    `;
  }

  get guildsTemplate() {
    return html`
      <h2>Guilds</h2>

      <simple-list id="guild-list">
        ${!this.guildMembers?.length ? html`
          <div class="list-item">
            <span class="empty-placeholder">
              <sl-icon library="material" name="block"></sl-icon>
              <span>${this.isSelf ? 'None yet!' : 'None visible'}</span>
            </span>
          </div>
        ` : this.guildMembers.map(guildMember => html`
          <div class="list-item">
            <q1-membership-chip
              .membership=${guildMember}
            ></q1-membership-chip>
          </div>
        `)}
      </simple-list>
    `;
  }

  get projectsTemplate() {
    return html`
      <h2>Projects</h2>

      <simple-list id="project-list">
        ${!this.projectMembers?.length ? html`
          <div class="list-item">
            <span class="empty-placeholder">
              <sl-icon library="material" name="block"></sl-icon>
              <span>${this.isSelf ? 'None yet!' : 'None visible'}</span>
            </span>
          </div>
        ` : this.projectMembers.map(projectMember => html`
          <div class="list-item">
            <q1-membership-chip
              .membership=${projectMember}
            ></q1-membership-chip>
          </div>
        `)}
      </simple-list>
    `;
  }

  /*=== UTILITY METHODS ===*/

  /**
   * Resorts the memberships array and returns a new sorted array.
   * For now the sort order is fixed:
   * - First by role_index descending
   * - Then by subject.name ascending
   */
  _sortMemberships(membershipsArray = []) {
    const sortedMemberships = _.orderBy(
      membershipsArray,
      ['role_index', 'subject.name'],
      ['desc', 'asc']
    );

    return sortedMemberships;
  }
  
}

customElements.define('member-profile-view', MemberProfileView);